import React, { useEffect, useMemo, useState } from "react";
import { Alert, DatePicker, Input, Modal, Select } from "antd";
import dayjs, { Dayjs } from "dayjs";
import useLang from "../../../hooks/useLang";
import {
	AddOrderStatusHistoryRequest,
	ResponsibleUser,
} from "../../../api/bulk-order-operations";
import { HistoryManagementCategorySettings } from "../../../utils/bulkOperationsConfig";
import { PRIMARY_BUTTON_STYLE } from "../components/styles";
import { getAvailableStatuses } from "./statuses";

/** dbo.StatusHistory.StatusChangeComment is varchar(4000). */
const MAX_COMMENT_LENGTH = 4000;

interface AddHistoryModalProps {
	open: boolean;
	orderId: number;
	isInternational: boolean;
	users: ResponsibleUser[];
	usersLoading: boolean;
	config: HistoryManagementCategorySettings;
	submitting: boolean;
	onCancel: () => void;
	onSubmit: (body: AddOrderStatusHistoryRequest) => void;
}

const userLabel = (user: ResponsibleUser) => {
	const name = `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim();
	return user.role ? `${name} (${user.role})` : name;
};

const AddHistoryModal = ({
	open,
	orderId,
	isInternational,
	users,
	usersLoading,
	config,
	submitting,
	onCancel,
	onSubmit,
}: AddHistoryModalProps) => {
	const { strings } = useLang("bulkOrderOperations");
	const copy = strings?.historyManagement;

	const [statusKey, setStatusKey] = useState<string | null>(null);
	const [userId, setUserId] = useState<number | null>(null);
	const [statusDate, setStatusDate] = useState<Dayjs | null>(dayjs());
	const [message, setMessage] = useState("");

	const statuses = useMemo(
		() => getAvailableStatuses(isInternational),
		[isInternational]
	);
	const selectedStatus = statuses.find((s) => s.key === statusKey) ?? null;

	// For an international order the responsible user is not the operator's choice: it is
	// this deployment's own Postman system account, from config.
	const effectiveUserId = isInternational
		? config.internationalStatusUserId
		: userId;
	const effectiveUserName = isInternational
		? config.internationalStatusUserName
		: (() => {
				const found = users.find((u) => u.id === userId);
				return found
					? `${found.firstName ?? ""} ${found.lastName ?? ""}`.trim()
					: "";
		  })();

	useEffect(() => {
		if (!open) return;
		setStatusKey(null);
		setUserId(null);
		setStatusDate(dayjs());
		setMessage("");
	}, [open]);

	// The message is pre-filled from the status but stays editable; the name suffix is not.
	useEffect(() => {
		if (selectedStatus) setMessage(selectedStatus.defaultComment);
	}, [selectedStatus]);

	const suffix = effectiveUserName ? `(${effectiveUserName})` : "";
	const fullComment = `${message}${suffix}`;
	const canSubmit =
		!!selectedStatus &&
		!!effectiveUserId &&
		fullComment.length <= MAX_COMMENT_LENGTH &&
		!submitting;

	const handleOk = () => {
		if (!selectedStatus || !effectiveUserId) return;
		onSubmit({
			orderId,
			statusId: selectedStatus.statusId,
			userId: effectiveUserId,
			// Local wall-clock with NO timezone suffix, deliberately not toISOString().
			// StatusHistory.StatusDate is a plain `datetime` that every other writer fills
			// with GETDATE(), i.e. server local time. Sending "…Z" would bind as UTC and
			// store a shifted time, putting the new row out of order against every existing
			// row — and AddBalance reads the most recent row's date as the delivered date.
			statusDate: statusDate
				? statusDate.format("YYYY-MM-DDTHH:mm:ss")
				: undefined,
			statusChangeComment: fullComment,
		});
	};

	return (
		<Modal
			open={open}
			title={copy?.addTitle ?? "Add history row"}
			okText={copy?.add ?? "Add"}
			cancelText={strings?.common?.cancel ?? "Cancel"}
			okButtonProps={{
				style: PRIMARY_BUTTON_STYLE,
				loading: submitting,
				disabled: !canSubmit,
			}}
			onOk={handleOk}
			onCancel={onCancel}
			destroyOnClose
		>
			<div className="flex flex-col gap-3 py-2">
				<div>
					<div className="font-medium mb-1">
						{copy?.fieldStatus ?? "Status"}
					</div>
					<Select
						className="w-full"
						value={statusKey}
						onChange={setStatusKey}
						placeholder={copy?.selectStatus ?? "Select a status"}
						options={statuses.map((s) => ({
							value: s.key,
							label: copy?.[s.labelKey] ?? s.fallbackLabel,
						}))}
					/>
					{selectedStatus && (
						<div className="text-xs text-gray-500 mt-1">
							{copy?.willWrite ?? "Will be saved as StatusId"}:{" "}
							<b>{selectedStatus.statusId}</b>
							{isInternational &&
								` · ${selectedStatus.internationalPair}`}
						</div>
					)}
				</div>

				<div>
					<div className="font-medium mb-1">
						{copy?.fieldUser ?? "Responsible user"}
					</div>
					<Select
						className="w-full"
						disabled={isInternational}
						loading={usersLoading}
						value={
							isInternational
								? config.internationalStatusUserId
								: userId
						}
						onChange={setUserId}
						showSearch
						optionFilterProp="label"
						placeholder={copy?.selectUser ?? "Select a user"}
						options={
							isInternational
								? [
										{
											value: config.internationalStatusUserId,
											label: config.internationalStatusUserName,
										},
								  ]
								: users.map((u) => ({
										value: u.id,
										label: userLabel(u),
								  }))
						}
					/>
					{isInternational && (
						<Alert
							className="mt-2"
							type="info"
							showIcon
							message={
								copy?.internationalUserNote ??
								"International orders are always recorded under this country's Postman system account."
							}
						/>
					)}
				</div>

				<div>
					<div className="font-medium mb-1">
						{copy?.fieldDate ?? "Date"}
					</div>
					<DatePicker
						className="w-full"
						showTime
						value={statusDate}
						onChange={setStatusDate}
					/>
				</div>

				<div>
					<div className="font-medium mb-1">
						{copy?.fieldComment ?? "Description (optional)"}
					</div>
					<Input.TextArea
						rows={3}
						value={message}
						onChange={(e) => setMessage(e.target.value)}
						placeholder={
							copy?.commentPlaceholder ??
							"Pre-filled from the status — edit if needed"
						}
					/>
					{/* The name suffix mirrors every other writer's convention and is not editable. */}
					<div className="text-xs text-gray-500 mt-1 font-mono break-all">
						{fullComment || "—"}
					</div>
				</div>
			</div>
		</Modal>
	);
};

export default AddHistoryModal;
